import React, { useState } from "react";
import useSWR from "swr";
import {
    Box,
    Button,
    Card,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Snackbar,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
    const protocol = window.location.protocol === "https:" ? "https" : "http";
    const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
    return `${protocol}://${baseUrl}`;
};

const fetcher = (url) =>
    axios.get(url, { withCredentials: true }).then((res) => res.data.data);

const branchesFetcher = (url) =>
    axios.get(url, { withCredentials: true }).then((res) => {
        const d = res.data.data;
        if (Array.isArray(d)) return d;
        if (d && typeof d === "object") return [d];
        return [];
    });

export const CustomerKategori = () => {
    const { user } = useSelector((state) => state.auth);

    const role = user?.role?.toLowerCase();
    const isSuperAdmin = role === "superadmin";
    const isAdmin = role === "admin";

    const fetchUrl = `${getApiBaseUrl()}/categorycustomer`;
    const { data: categorys, error: CategoryError, mutate } = useSWR(fetchUrl, fetcher);

    const { data: branches } = useSWR(
        isSuperAdmin ? `${getApiBaseUrl()}/cabangbyrole?bentuk=daftar` : null,
        branchesFetcher
    );

    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState({ namaKategoriCustomer: "", cabangUuid: "" });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const getCabangNama = (cabanguuid) => {
        if (!cabanguuid || !Array.isArray(branches)) return null;
        const found = branches.find((b) => b.uuid === cabanguuid);
        return found ? found.namacabang : null;
    };

    const handleOpen = () => {
        setFormData({
            namaKategoriCustomer: "",
            cabangUuid: isAdmin ? (user.cabanguuid || "") : "",
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setFormData({ namaKategoriCustomer: "", cabangUuid: "" });
        setIsEditing(false);
        setEditId(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.namaKategoriCustomer.trim()) {
            showSnackbar("Nama kategori wajib diisi", "error");
            return;
        }

        const payload = {
            namaKategoriCustomer: formData.namaKategoriCustomer,
            ...(isSuperAdmin && { cabangUuid: formData.cabangUuid || null }),
        };

        try {
            if (isEditing) {
                await axios.patch(
                    `${getApiBaseUrl()}/updatecategorycustomer/${editId}`,
                    payload,
                    { withCredentials: true }
                );
                showSnackbar("Kategori berhasil diperbarui");
            } else {
                await axios.post(
                    `${getApiBaseUrl()}/createcategorycustomer`,
                    payload,
                    { withCredentials: true }
                );
                showSnackbar("Kategori berhasil ditambahkan");
            }
            mutate();
            handleClose();
        } catch (error) {
            const msg = error.response?.data?.message || "Terjadi kesalahan";
            showSnackbar(msg, "error");
        }
    };

    const handleEdit = (kategori) => {
        setFormData({
            namaKategoriCustomer: kategori.namaKategoriCustomer,
            cabangUuid: kategori.cabangUuid || "",
        });
        setEditId(kategori.uuid);
        setIsEditing(true);
        setOpen(true);
    };

    const handleDeleteConfirm = (id) => {
        setConfirmDelete({ open: true, id });
    };

    const handleDeleteCancel = () => {
        setConfirmDelete({ open: false, id: null });
    };

    const handleDelete = async () => {
        try {
            await axios.delete(
                `${getApiBaseUrl()}/deletecategorycustomer/${confirmDelete.id}`,
                { withCredentials: true }
            );
            mutate();
            showSnackbar("Kategori berhasil dihapus");
        } catch (error) {
            const msg = error.response?.data?.message || "Gagal menghapus";
            showSnackbar(msg, "error");
        } finally {
            handleDeleteCancel();
        }
    };

    if (CategoryError) {
        return (
            <Typography color="error">
                Gagal memuat data: {CategoryError.message}
            </Typography>
        );
    }

    if (!categorys) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 2, overflowX: "auto" }}>
            <Typography variant="h6" gutterBottom>
                List Kategori Customer
            </Typography>

            <Button variant="contained" color="primary" onClick={handleOpen} sx={{ mb: 2 }}>
                Tambah
            </Button>

            <Card>
                <Box sx={{ overflowX: "auto" }}>
                    <Table sx={{ minWidth: 600 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>Nama Kategori</TableCell>
                                {isSuperAdmin && <TableCell>Cabang</TableCell>}
                                <TableCell>Aksi</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categorys.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={isSuperAdmin ? 4 : 3} align="center">
                                        Belum ada data kategori
                                    </TableCell>
                                </TableRow>
                            ) : (
                                categorys.map((kategori, index) => {
                                    const namaCabang = getCabangNama(kategori.cabangUuid);
                                    return (
                                        <TableRow key={kategori.uuid}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{kategori.namaKategoriCustomer}</TableCell>
                                            {isSuperAdmin && (
                                                <TableCell>
                                                    {namaCabang ? (
                                                        <Chip label={namaCabang} size="small" color="primary" variant="outlined" />
                                                    ) : (
                                                        <Chip label="Semua Cabang" size="small" variant="outlined" />
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleEdit(kategori)}
                                                    sx={{ mr: 1 }}
                                                >
                                                    Edit
                                                </Button>
                                                {(isAdmin || isSuperAdmin) && (
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteConfirm(kategori.uuid)}
                                                    >
                                                        Hapus
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Card>

            <Modal open={open} onClose={handleClose}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 340,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        {isEditing ? "Edit Kategori Customer" : "Tambah Kategori Customer"}
                    </Typography>

                    <TextField
                        label="Nama Kategori"
                        name="namaKategoriCustomer"
                        value={formData.namaKategoriCustomer}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                    />

                    {isSuperAdmin && (
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Cabang</InputLabel>
                            <Select
                                name="cabangUuid"
                                value={formData.cabangUuid}
                                onChange={handleChange}
                                label="Cabang"
                            >
                                <MenuItem value="">Semua Cabang</MenuItem>
                                {(Array.isArray(branches) ? branches : []).map((branch) => (
                                    <MenuItem key={branch.uuid} value={branch.uuid}>
                                        {branch.namacabang}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {isAdmin && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Kategori akan ditambahkan ke cabang Anda.
                        </Typography>
                    )}

                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                        <Button variant="outlined" color="inherit" onClick={handleClose} fullWidth>
                            Batal
                        </Button>
                        <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth>
                            {isEditing ? "Update" : "Simpan"}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Dialog open={confirmDelete.open} onClose={handleDeleteCancel}>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Yakin ingin menghapus kategori ini? Data yang dihapus tidak dapat dikembalikan.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Batal</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};